import React, { useState } from 'react'
import { Upload, Video, Mic, Instagram, Twitter, Music } from 'lucide-react'

const NILProfileStep = ({ data, onChange, onNext, onBack }) => {
  const [uploadStates, setUploadStates] = useState({
    photo: false,
    video: false,
    voice: false
  })
  const [isRecording, setIsRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  
  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Prepare social media data
    const socialMedia = {}
    if (data.instagramHandle) socialMedia.instagram = data.instagramHandle
    if (data.twitterHandle) socialMedia.twitter = data.twitterHandle
    if (data.tiktokHandle) socialMedia.tiktok = data.tiktokHandle
    
    // Update form data with social media
    onChange({ socialMedia })
    
    onNext()
  }

  const updateField = (field, value) => {
    onChange({ [field]: value })
  }

  const handleFileUpload = (type, file) => {
    if (file) {
      setUploadStates(prev => ({ ...prev, [type]: true }))
      updateField(`${type}File`, file)
      updateField(`${type}Url`, URL.createObjectURL(file))
    }
  }

  const startRecording = () => {
    setIsRecording(true)
    setRecordingTime(0)
    // In production, this would start actual audio recording
    const interval = setInterval(() => {
      setRecordingTime(prev => {
        if (prev >= 30) {
          stopRecording()
          return 30
        }
        return prev + 1
      })
    }, 1000)
    setTimeout(() => clearInterval(interval), 31000)
  }

  const stopRecording = () => {
    setIsRecording(false)
    setUploadStates(prev => ({ ...prev, voice: true }))
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="view active">
      <div className="panel">
        <div className="panel-head" style={{
          background: 'linear-gradient(135deg,var(--navy) 0%,var(--navy3) 100%)',
          padding: '32px 36px 28px',
          borderBottom: '3px solid var(--gold)'
        }}>
          <div className="panel-title" style={{
            fontFamily: 'var(--font-d)',
            fontSize: '26px',
            color: 'var(--white)',
            marginBottom: '6px'
          }}>
            Load Your NIL Profile
          </div>
          <div className="panel-subtitle" style={{
            fontSize: '14px',
            color: 'rgba(255,255,255,0.55)',
            fontWeight: '300'
          }}>
            Your Name, Image & Likeness assets that will be registered on the Story Protocol blockchain as your Intellectual Property Account (IPA).
          </div>
        </div>
        
        <div className="panel-body" style={{ padding: '36px' }}>
          <div className="info-bar" style={{
            background: 'rgba(201,168,76,0.07)',
            border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 'var(--r)',
            padding: '14px 18px',
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
            marginBottom: '24px'
          }}>
            <div className="icon" style={{ fontSize: '18px', flexShrink: 0, marginTop: '1px' }}>⛓️</div>
            <p style={{ fontSize: '13px', color: 'var(--text2)', lineHeight: '1.6' }}>
              These assets become your on-chain commercial identity. Once registered, your NIL is protected by Story Protocol's Programmable IP License (PIL) framework. <strong>IPST holds a PIL sub-license</strong> that enables the tracking stock yield mechanism.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* NIL Identity Assets */}
            <div className="sdiv" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              margin: '28px 0 20px'
            }}>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
              <div className="sdiv-label" style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--text3)',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                whiteSpace: 'nowrap'
              }}>
                NIL identity assets
              </div>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
            </div>

            <div className="form-grid" style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '20px'
            }}>
              <div className="fg form-full">
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  NIL display name — how your brand will appear on-chain <span style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <input
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  type="text"
                  value={data.displayName || ''}
                  onChange={(e) => updateField('displayName', e.target.value)}
                  placeholder="e.g. Bryson DeChambeau — LIV Golf"
                  required
                />
                <div className="field-hint" style={{
                  fontSize: '11px',
                  color: 'var(--text3)',
                  marginTop: '5px'
                }}>
                  This is your public commercial identity as it will appear to fans, sponsors, and on the DOGE.live platform.
                </div>
              </div>
              <div className="fg form-full">
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Commercial bio — for sponsor display and oracle event descriptions <span style={{ color: 'var(--gold)', marginLeft: '2px' }}>*</span>
                </label>
                <textarea
                  className="fi"
                  style={{
                    width: '100%',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--r)',
                    padding: '11px 14px',
                    fontFamily: 'var(--font-b)',
                    fontSize: '14px',
                    color: 'var(--text)',
                    background: 'var(--white)',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                  value={data.bio || ''}
                  onChange={(e) => updateField('bio', e.target.value)}
                  placeholder="World-ranked athlete. Describe your sport, achievements, and commercial appeal…"
                  required
                />
              </div>
            </div>

            {/* Official NIL Photo */}
            <div className="sdiv" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              margin: '28px 0 20px'
            }}>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
              <div className="sdiv-label" style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--text3)',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                whiteSpace: 'nowrap'
              }}>
                4A · Official NIL photo
              </div>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
            </div>

            <div 
              className={`nil-upload ${uploadStates.photo ? 'has-file' : ''}`}
              onClick={() => document.getElementById('nil-photo-file').click()}
              style={{
                border: '2px dashed var(--border)',
                borderRadius: 'var(--r2)',
                padding: '40px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: uploadStates.photo ? 'rgba(13,158,124,0.05)' : 'var(--off)',
                borderColor: uploadStates.photo ? 'var(--teal)' : 'var(--border)'
              }}
            >
              <input
                id="nil-photo-file"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handleFileUpload('photo', e.target.files[0])}
                style={{ display: 'none' }}
              />
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                {uploadStates.photo ? '✓' : <Upload size={48} />}
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--text)',
                marginBottom: '8px'
              }}>
                {uploadStates.photo ? 'Photo uploaded' : 'Upload official athlete photo'}
              </div>
              <div style={{
                fontSize: '13px',
                color: 'var(--text3)',
                lineHeight: '1.5',
                maxWidth: '400px',
                margin: '0 auto'
              }}>
                High-resolution JPG or PNG · Minimum 800×800px · Professional or action shot · No filters or heavy editing<br/>
                This photo will be displayed on your DOGE.live profile and IPST tracking stock page.
              </div>
            </div>

            {/* NIL Head Movement Video */}
            <div className="sdiv" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              margin: '28px 0 20px'
            }}>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
              <div className="sdiv-label" style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--text3)',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                whiteSpace: 'nowrap'
              }}>
                4B · NIL head movement video
              </div>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
            </div>

            <div className="warn-bar" style={{
              background: 'rgba(201,168,76,0.08)',
              border: '1px solid rgba(201,168,76,0.25)',
              borderRadius: 'var(--r)',
              padding: '14px 18px',
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start',
              marginBottom: '20px'
            }}>
              <Video size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
              <p style={{ fontSize: '13px', color: 'var(--text2)', lineHeight: '1.6' }}>
                Record a short video of your face moving <strong>up and down</strong>, then <strong>left and right</strong>. This is your on-chain biometric anchor — it links your verified identity to your NIL and is used to authenticate future commercial activations and oracle events.
              </p>
            </div>

            <div 
              className={`kyc-card ${uploadStates.video ? 'done' : ''}`}
              onClick={() => document.getElementById('nil-video-file').click()}
              style={{
                maxWidth: '400px',
                border: '1.5px solid var(--border)',
                borderRadius: 'var(--r2)',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: uploadStates.video ? 'rgba(13,158,124,0.05)' : 'var(--white)',
                borderColor: uploadStates.video ? 'var(--teal)' : 'var(--border)'
              }}
            >
              <input
                id="nil-video-file"
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                onChange={(e) => handleFileUpload('video', e.target.files[0])}
                style={{ display: 'none' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: uploadStates.video ? 'var(--teal)' : 'var(--navy)',
                  color: 'var(--white)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px'
                }}>
                  {uploadStates.video ? '✓' : <Video size={16} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--text)',
                    marginBottom: '4px'
                  }}>
                    Record NIL head video
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--text3)'
                  }}>
                    Face camera · good lighting · up-down then left-right · 5–10 seconds
                  </div>
                </div>
              </div>
              <div className={`kyc-status ${uploadStates.video ? 'done' : 'pending'}`} style={{
                fontSize: '12px',
                color: uploadStates.video ? 'var(--teal)' : 'var(--text3)',
                marginTop: '8px'
              }}>
                {uploadStates.video ? '✓ Recorded' : 'Tap to record'}
              </div>
            </div>

            {/* Voice Signature Recording */}
            <div className="sdiv" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              margin: '28px 0 20px'
            }}>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
              <div className="sdiv-label" style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--text3)',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                whiteSpace: 'nowrap'
              }}>
                4C · Voice signature recording
              </div>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
            </div>

            <div className="warn-bar" style={{
              background: 'rgba(201,168,76,0.08)',
              border: '1px solid rgba(201,168,76,0.25)',
              borderRadius: 'var(--r)',
              padding: '14px 18px',
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start',
              marginBottom: '20px'
            }}>
              <Mic size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
              <p style={{ fontSize: '13px', color: 'var(--text2)', lineHeight: '1.6' }}>
                Read the following statement clearly: <em>"I, [your full name], authorize IPST and DOGE.live to register my Name, Image, and Likeness on the Story Protocol blockchain under the PIL sub-license terms I have reviewed and accepted. Today's date is [today's date]."</em>
              </p>
            </div>

            <div style={{
              background: 'var(--navy)',
              borderRadius: 'var(--r2)',
              padding: '24px',
              marginBottom: '20px'
            }}>
              <div style={{
                fontSize: '12px',
                color: 'rgba(255,255,255,0.5)',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: '16px',
                textAlign: 'center'
              }}>
                Voice signature
              </div>
              <div className="rec-viz" style={{
                height: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2px',
                marginBottom: '12px'
              }}>
                {isRecording ? Array.from({length: 20}, (_, i) => (
                  <div
                    key={i}
                    style={{
                      width: '3px',
                      height: `${Math.random() * 30 + 10}px`,
                      background: 'var(--gold)',
                      borderRadius: '2px',
                      animation: 'pulse 1s infinite'
                    }}
                  />
                )) : (
                  <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '13px' }}>
                    {uploadStates.voice ? 'Recording complete' : 'Press record to begin'}
                  </div>
                )}
              </div>
              {isRecording && (
                <div style={{
                  fontFamily: 'var(--font-m)',
                  fontSize: '20px',
                  color: 'var(--gold)',
                  marginBottom: '12px',
                  textAlign: 'center'
                }}>
                  {formatTime(recordingTime)}
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  type="button"
                  className="btn btn-outline-gold btn-sm"
                  onClick={isRecording ? stopRecording : startRecording}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '9px 18px',
                    borderRadius: 'var(--r)',
                    fontFamily: 'var(--font-b)',
                    fontSize: '13px',
                    fontWeight: '600',
                    letterSpacing: '0.02em',
                    cursor: 'pointer',
                    border: '1.5px solid var(--gold)',
                    background: 'transparent',
                    color: 'var(--gold)',
                    transition: 'all 0.2s'
                  }}
                >
                  {isRecording ? '⏹ Stop Recording' : '🎙️ Start Recording'}
                </button>
                {uploadStates.voice && !isRecording && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '9px 18px',
                      borderRadius: 'var(--r)',
                      fontFamily: 'var(--font-b)',
                      fontSize: '13px',
                      fontWeight: '600',
                      letterSpacing: '0.02em',
                      cursor: 'pointer',
                      border: '1.5px solid var(--border)',
                      background: 'transparent',
                      color: 'var(--text2)',
                      transition: 'all 0.2s'
                    }}
                  >
                    ▶ Play Back
                  </button>
                )}
              </div>
            </div>

            {uploadStates.voice && (
              <div style={{
                color: 'var(--teal)',
                fontSize: '13px',
                fontWeight: '600',
                textAlign: 'center',
                marginBottom: '20px'
              }}>
                ✓ Voice signature recorded
              </div>
            )}

            {/* Social Media Handles */}
            <div className="sdiv" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              margin: '28px 0 20px'
            }}>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
              <div className="sdiv-label" style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--text3)',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                whiteSpace: 'nowrap'
              }}>
                Social media & commercial handles
              </div>
              <div className="sdiv-line" style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
            </div>

            <div className="form-grid-3" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '16px'
            }}>
              <div className="fg">
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  Instagram
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text3)',
                    fontSize: '14px'
                  }}>
                    @
                  </span>
                  <input
                    className="fi"
                    style={{
                      width: '100%',
                      border: '1.5px solid var(--border)',
                      borderRadius: 'var(--r)',
                      padding: '11px 14px 11px 28px',
                      fontFamily: 'var(--font-b)',
                      fontSize: '14px',
                      color: 'var(--text)',
                      background: 'var(--white)',
                      transition: 'border-color 0.2s, box-shadow 0.2s'
                    }}
                    type="text"
                    value={data.instagramHandle || ''}
                    onChange={(e) => updateField('instagramHandle', e.target.value)}
                    placeholder="handle"
                  />
                </div>
              </div>
              <div className="fg">
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  X (Twitter)
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text3)',
                    fontSize: '14px'
                  }}>
                    @
                  </span>
                  <input
                    className="fi"
                    style={{
                      width: '100%',
                      border: '1.5px solid var(--border)',
                      borderRadius: 'var(--r)',
                      padding: '11px 14px 11px 28px',
                      fontFamily: 'var(--font-b)',
                      fontSize: '14px',
                      color: 'var(--text)',
                      background: 'var(--white)',
                      transition: 'border-color 0.2s, box-shadow 0.2s'
                    }}
                    type="text"
                    value={data.twitterHandle || ''}
                    onChange={(e) => updateField('twitterHandle', e.target.value)}
                    placeholder="handle"
                  />
                </div>
              </div>
              <div className="fg">
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--text2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '6px'
                }}>
                  TikTok
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text3)',
                    fontSize: '14px'
                  }}>
                    @
                  </span>
                  <input
                    className="fi"
                    style={{
                      width: '100%',
                      border: '1.5px solid var(--border)',
                      borderRadius: 'var(--r)',
                      padding: '11px 14px 11px 28px',
                      fontFamily: 'var(--font-b)',
                      fontSize: '14px',
                      color: 'var(--text)',
                      background: 'var(--white)',
                      transition: 'border-color 0.2s, box-shadow 0.2s'
                    }}
                    type="text"
                    value={data.tiktokHandle || ''}
                    onChange={(e) => updateField('tiktokHandle', e.target.value)}
                    placeholder="handle"
                  />
                </div>
              </div>
            </div>

            <div className="btn-actions" style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '28px',
              paddingTop: '24px',
              borderTop: '1px solid var(--border)'
            }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onBack}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '13px 28px',
                  borderRadius: 'var(--r)',
                  fontFamily: 'var(--font-b)',
                  fontSize: '14px',
                  fontWeight: '600',
                  letterSpacing: '0.02em',
                  cursor: 'pointer',
                  border: '1.5px solid var(--border)',
                  background: 'transparent',
                  color: 'var(--text2)',
                  transition: 'all 0.2s'
                }}
              >
                ← Back
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '13px 28px',
                  borderRadius: 'var(--r)',
                  fontFamily: 'var(--font-b)',
                  fontSize: '14px',
                  fontWeight: '600',
                  letterSpacing: '0.02em',
                  cursor: 'pointer',
                  border: 'none',
                  transition: 'all 0.2s',
                  background: 'var(--gold)',
                  color: 'var(--navy)',
                  boxShadow: '0 4px 16px rgba(201,168,76,0.3)'
                }}
              >
                Continue to Story Protocol →
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default NILProfileStep
